import React from 'react';
import { KeyedMutator } from 'swr';
import Table from 'ui/elements/tables/Table/Table';
import CompanyColumn from 'domain/shared/TableComponents/CompanyColumn';
import Chip from 'ui/elements/Chip';
import ChipList from 'ui/elements/Chip/ChipList';
import LabelChip from 'ui/domain/Chips/LabelChip';
import FundingStageChip from 'ui/domain/Chips/FundingStageChip';
import TruncatedList from 'util/components/TruncatedList';
import ButtonList from 'ui/elements/buttons/ButtonList';
import FollowButton from 'domain/companies/Follow/FollowButton';
import CompanyShortlistPicker from 'domain/Shortlists/CompanyShortlistPicker';
import { ShortlistedCompanyCard } from 'apis/CompanyAPI/users/useShortlistedCompanies';
import { List } from 'types/api';
import { Shortlist } from 'types/user';
import { companyUrls } from 'urls';
import { timeSince } from 'util/dateUtils';
import { notEmpty } from 'util/arrayUtils';
import { latestActivity } from './latestActivity';

interface Props {
  companies: ShortlistedCompanyCard[];
  shortlists: Shortlist[];
  setShortlists: KeyedMutator<List<Shortlist>>;
  isLoading: boolean;
  emptyMessage: string;
}

const columnClassNames = [undefined, 'u-desktop-up-only', 'u-tablet-up-only', 'u-tablet-up-only', 'u-desktop-up-only'];

export default function CollectionCompaniesTable({
  companies,
  shortlists,
  setShortlists,
  isLoading,
  emptyMessage,
}: Props) {
  const listNames = new Map(shortlists.map(list => [list.id, list.name]));

  return (
    <Table
      isLoading={isLoading}
      hasMenu
      hasClickableRows
      columnClassNames={columnClassNames}
      infoMessages={{ noResults: emptyMessage }}
    >
      <Table.Header>
        <Table.HeaderCell>Company</Table.HeaderCell>
        <Table.HeaderCell>Lists</Table.HeaderCell>
        <Table.HeaderCell>Funding</Table.HeaderCell>
        <Table.HeaderCell>Last activity</Table.HeaderCell>
        <Table.HeaderCell>
          <span />
        </Table.HeaderCell>
      </Table.Header>
      <Table.Body>
        {companies.map(({ card, shortlistIds, addedAt }) => {
          const activity = latestActivity(card);
          return (
            <Table.Row
              key={card.details.id}
              href={companyUrls.overview(card.details.slug, 'profile')}
              menu={
                <CompanyShortlistPicker
                  company={card.details}
                  shortlists={shortlists}
                  setShortlists={setShortlists}
                  menuItemHeight="50px"
                  dropdownWidth="250px"
                />
              }
            >
              <Table.DataCell>
                <CompanyColumn
                  companyName={card.details.name}
                  logoURL={card.details.logoURL}
                  logoSize={40}
                  subtitle={card.industries[0]?.name}
                />
              </Table.DataCell>
              <Table.DataCell>
                <ChipList>
                  <TruncatedList
                    values={shortlistIds
                      .map(id => listNames.get(id))
                      .filter(notEmpty)
                      .map(name => ({ name }))}
                    limit={2}
                    renderItem={list => <LabelChip label={list.name} />}
                    renderExpandButton={(list, onClick) => <LabelChip onClick={onClick} label={list.name} />}
                  />
                </ChipList>
              </Table.DataCell>
              <Table.DataCell>
                <ChipList>
                  {card.fundingStage && <FundingStageChip label={card.fundingStage.name} />}
                  {card.hasActiveFundingRound && <Chip color="orange" label="Raising now" />}
                </ChipList>
              </Table.DataCell>
              <Table.DataCell>
                {activity ? (
                  <>
                    <div className="text-body">{activity.label}</div>
                    <div className="text-metadata">{timeSince(activity.at)}</div>
                  </>
                ) : (
                  <>
                    <div className="text-body">No activity yet</div>
                    <div className="text-metadata">Added · {timeSince(addedAt)}</div>
                  </>
                )}
              </Table.DataCell>
              <Table.DataCell isLinkDisabled>
                <ButtonList align="right">
                  <FollowButton
                    mode="compact"
                    kind="secondary"
                    size="small"
                    company={card.details}
                    trackingKey="collections-table-follow"
                  />
                </ButtonList>
              </Table.DataCell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}
